import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, defer, EMPTY, Observable, Subject, Subscription, interval } from 'rxjs';
import { distinctUntilChanged, expand, map, reduce } from 'rxjs/operators';
import {
  HtsClassificationJobsService,
  HtsJob,
  HtsJobAttentionItem,
  HtsJobsPage,
  HtsJobsAttentionSummary,
} from './hts-classification-jobs.service';

export interface HtsJobAttentionTransition {
  determinationId: string;
  status: 'ready' | 'needs_answer';
  item: HtsJobAttentionItem;
}

/** One poller for the whole app. A dialog's job-list read shares its timer. */
@Injectable({ providedIn: 'root' })
export class HtsClassificationJobsStore implements OnDestroy {
  private readonly summarySubject = new BehaviorSubject<HtsJobsAttentionSummary | null>(null);
  readonly summary$ = this.summarySubject.asObservable();
  private readonly jobsSubject = new BehaviorSubject<HtsJob[]>([]);
  readonly jobs$ = new Observable<HtsJob[]>(observer => {
    this.ensureCurrentSession();
    this.jobListSubscribers++;
    const subscription = this.jobsSubject.subscribe(observer);
    const wasPolling = this.pollingRequested;
    this.startPolling();
    if (wasPolling && !this.jobsRequestedForToken) this.refreshJobList();
    return () => {
      subscription.unsubscribe();
      this.jobListSubscribers--;
    };
  });
  private readonly rfqGroups = new Map<string, { subject: BehaviorSubject<HtsJob[]>; subscribers: number }>();
  private readonly coverageErrorSubject = new BehaviorSubject<string | null>(null);
  readonly coverageError$ = this.coverageErrorSubject.asObservable();
  private readonly transitionSubject = new Subject<HtsJobAttentionTransition>();
  readonly transition$ = this.transitionSubject.asObservable();

  private pollSubscription: Subscription | null = null;
  private summaryRequest: Subscription | null = null;
  private jobsRequest: Subscription | null = null;
  private summaryInFlight = false;
  private jobsInFlight = false;
  private pollingRequested = false;
  private jobWatchers = 0;
  private jobListSubscribers = 0;
  private jobsRequestedForToken = false;
  private lastToken: string | null = null;
  private previousStatuses: Map<string, string> | null = null;
  private refreshPending = false;
  private jobsEnabled: boolean | null = null;

  constructor(private readonly client: HtsClassificationJobsService) {}

  private readonly onVisibilityChange = (): void => {
    if (!this.pollingRequested) return;
    if (document.visibilityState === 'visible') this.beginPolling();
    else this.pausePolling();
  };

  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key !== 'token' && event.key !== null) return;
    this.ensureCurrentSession();
    this.tick();
  };

  startPolling(): void {
    if (this.pollingRequested) return;
    this.pollingRequested = true;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('storage', this.onStorage);
    this.beginPolling();
  }

  stopPolling(): void {
    if (!this.pollingRequested) return;
    this.pollingRequested = false;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('storage', this.onStorage);
    this.pausePolling();
    this.clearState();
  }

  refreshNow(): void {
    if (this.summaryInFlight || this.jobsInFlight) {
      this.refreshPending = true;
      return;
    }
    this.tick();
  }

  /** Projects the last complete paginated job snapshot for this RFQ. */
  jobsForRfq$(rfqId: number | string): Observable<HtsJob[]> {
    const key = String(rfqId);
    return new Observable(observer => {
      this.ensureCurrentSession();
      let group = this.rfqGroups.get(key);
      const created = !group;
      if (!group) {
        group = { subject: new BehaviorSubject<HtsJob[]>(this.jobsSubject.value.filter(job =>
          job.rfqId != null && String(job.rfqId) === key,
        )), subscribers: 0 };
        this.rfqGroups.set(key, group);
      }
      group.subscribers++;
      const subscription = group.subject.subscribe(observer);
      const wasPolling = this.pollingRequested;
      this.startPolling();
      if (wasPolling && created && !this.jobsRequestedForToken) this.refreshJobList();
      return () => {
        subscription.unsubscribe();
        group.subscribers--;
        if (group.subscribers === 0) {
          this.rfqGroups.delete(key);
          group.subject.complete();
        }
      };
    });
  }

  /** The full latest state is fetched only while a caller watches a job. */
  watchJob$(determinationId: string): Observable<HtsJob | null> {
    return new Observable(observer => {
      this.ensureCurrentSession();
      this.jobWatchers++;
      const subscription = this.jobsSubject.pipe(
        map(jobs => jobs.find(job => job.determinationId === determinationId) ?? null),
        distinctUntilChanged((a, b) => a?.status === b?.status && a?.updatedAt === b?.updatedAt),
      ).subscribe(observer);
      const wasPolling = this.pollingRequested;
      this.startPolling();
      if (wasPolling && !this.jobsRequestedForToken) this.refreshJobList();
      return () => {
        subscription.unsubscribe();
        this.jobWatchers--;
      };
    });
  }

  private beginPolling(): void {
    if (this.pollSubscription || document.visibilityState !== 'visible') return;
    this.tick();
    this.pollSubscription = interval(15_000).subscribe(() => this.tick());
  }

  private pausePolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    this.summaryRequest?.unsubscribe();
    this.summaryRequest = null;
    this.summaryInFlight = false;
    this.jobsRequest?.unsubscribe();
    this.jobsRequest = null;
    this.jobsInFlight = false;
    this.refreshPending = false;
  }

  private tick(): void {
    if (!this.pollingRequested || document.visibilityState !== 'visible') return;
    this.ensureCurrentSession();
    if (!this.lastToken) return;
    if (!this.summaryInFlight) {
      this.summaryInFlight = true;
      const requestToken = this.lastToken;
      this.summaryRequest = this.client.getAttentionSummary(true).subscribe({
        next: summary => {
          if (localStorage.getItem('token') !== requestToken) {
            this.ensureCurrentSession();
            return;
          }
          if ('enabled' in summary && summary.enabled === false) {
            this.jobsEnabled = false;
            this.coverageErrorSubject.next(null);
            this.jobsRequest?.unsubscribe();
            this.jobsRequest = null;
            this.jobsInFlight = false;
            this.previousStatuses = null;
            this.jobsRequestedForToken = false;
            this.summarySubject.next(null);
            this.acceptJobs([]);
            return;
          }
          if (!this.jobsEnabled) this.coverageErrorSubject.next(null);
          this.jobsEnabled = true;
          this.acceptSummary(summary as HtsJobsAttentionSummary);
          this.refreshJobList();
        },
        error: () => {
          this.finishSummaryRequest();
          this.refreshJobList();
        },
        complete: () => this.finishSummaryRequest(),
      });
      if (this.summaryRequest.closed) this.summaryRequest = null;
    }
  }

  private ensureCurrentSession(): void {
    const token = localStorage.getItem('token');
    if (token === this.lastToken) return;
    this.summaryRequest?.unsubscribe();
    this.summaryRequest = null;
    this.summaryInFlight = false;
    this.jobsRequest?.unsubscribe();
    this.jobsRequest = null;
    this.jobsInFlight = false;
    this.refreshPending = false;
    this.lastToken = token;
    this.clearState();
  }

  private refreshJobList(): void {
    if (this.jobsEnabled !== true || !this.pollingRequested || document.visibilityState !== 'visible' ||
        !localStorage.getItem('token') ||
        (this.jobWatchers === 0 && this.jobListSubscribers === 0 && this.rfqGroups.size === 0) || this.jobsInFlight) return;
    this.jobsInFlight = true;
    this.jobsRequestedForToken = true;
    const requestToken = this.lastToken;
    this.jobsRequest = this.readAllJobs().subscribe({
      next: jobs => {
        if (localStorage.getItem('token') !== requestToken) {
          this.ensureCurrentSession();
          return;
        }
        this.acceptJobs(jobs);
        this.coverageErrorSubject.next(null);
      },
      error: () => {
        if (localStorage.getItem('token') !== requestToken) {
          this.ensureCurrentSession();
          return;
        }
        this.coverageErrorSubject.next('Classification statuses could not be fully refreshed. Showing the last complete update. [where: HtsClassificationJobsStore.refreshJobList]');
        this.finishJobsRequest();
      },
      complete: () => this.finishJobsRequest(),
    });
    if (this.jobsRequest.closed) this.jobsRequest = null;
  }

  private readAllJobs(): Observable<HtsJob[]> {
    return defer(() => {
      const seenCursors = new Set<string>();
      return this.client.listJobsPage(undefined, true).pipe(
        expand(page => {
          this.assertValidPage(page);
          if (page.nextCursor === null) return EMPTY;
          if (seenCursors.has(page.nextCursor)) throw new Error('Repeated HTS job cursor');
          seenCursors.add(page.nextCursor);
          return this.client.listJobsPage(page.nextCursor, true);
        }),
        reduce((all, page) => all.concat(page.items), [] as HtsJob[]),
      );
    });
  }

  private assertValidPage(page: HtsJobsPage): void {
    if (!page || !Array.isArray(page.items) || page.items.length > 500 ||
        (page.nextCursor !== null && (typeof page.nextCursor !== 'string' || !page.nextCursor))) {
      throw new Error('Invalid HTS job page');
    }
  }

  private acceptJobs(jobs: HtsJob[]): void {
    this.jobsSubject.next(jobs);
    const byRfq = new Map<string, HtsJob[]>();
    for (const job of jobs) {
      if (job.rfqId == null) continue;
      const key = String(job.rfqId);
      if (!byRfq.has(key)) byRfq.set(key, []);
      byRfq.get(key).push(job);
    }
    for (const [key, group] of this.rfqGroups) group.subject.next(byRfq.get(key) ?? []);
  }

  private acceptSummary(summary: HtsJobsAttentionSummary): void {
    const current = new Map(summary.items.map(item => [item.determinationId, item.status]));
    if (this.previousStatuses) {
      for (const item of summary.items) {
        if ((item.status === 'ready' || item.status === 'needs_answer') &&
            this.previousStatuses.get(item.determinationId) !== item.status) {
          this.transitionSubject.next({ determinationId: item.determinationId, status: item.status, item });
        }
      }
    }
    this.previousStatuses = current;
    this.summarySubject.next(summary);
  }

  private finishSummaryRequest(): void {
    this.summaryRequest = null;
    this.summaryInFlight = false;
    this.flushPending();
  }

  private finishJobsRequest(): void {
    this.jobsRequest = null;
    this.jobsInFlight = false;
    this.flushPending();
  }

  private flushPending(): void {
    if (!this.refreshPending || this.summaryInFlight || this.jobsInFlight) return;
    this.refreshPending = false;
    this.tick();
  }

  private clearState(): void {
    this.previousStatuses = null;
    this.jobsEnabled = null;
    this.jobsRequestedForToken = false;
    this.coverageErrorSubject.next(null);
    this.summarySubject.next(null);
    this.jobsSubject.next([]);
    for (const group of this.rfqGroups.values()) group.subject.next([]);
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.transitionSubject.complete();
    this.summarySubject.complete();
    this.jobsSubject.complete();
    this.coverageErrorSubject.complete();
  }
}
